import { useEffect, useState } from "react";
import { Sparkles, AlertTriangle, TrendingUp, Loader2, Settings2, ChevronDown, Send, Pin, PinOff } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import BulkTransferCascadeModal from "./BulkTransferCascadeModal";

const fmtEur = (n) => `${Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const SOURCE_LABEL = {
  vademecum: "vademécum",
  linear: "lineal",
  avg: "medio",
  cn: "CN",
  excluded: "excluida",
  none: "sin dto",
};

/**
 * Panel económico de proveedores del estudio.
 * - Muestra por lab: total compra PVL, descuento medio, beneficio óptimo y alertas de volumen.
 * - Botón "Optimizar" → pide al backend la asignación más rentable por molécula.
 *
 * Props:
 *   dispId: string
 *   onConfigure: (lab: string) => void  // abre el modal de configuración para ese lab
 *   refreshKey: number  // bump para forzar reload
 */
export default function SupplierEconomicsPanel({ dispId, onConfigure, refreshKey = 0 }) {
  const [econ, setEcon] = useState(null);
  const [opt, setOpt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [optimizing, setOptimizing] = useState(false);
  const [keepBusy, setKeepBusy] = useState(null);

  // "Mantener proveedor actual": excluye la molécula de la sugerencia de
  // opción más rentable (motivos comerciales para no moverla).
  const toggleKeepCurrent = async (gh, keep) => {
    setKeepBusy(gh);
    try {
      await api.post("/best-supplier/keep-flags", { gh, keep });
      setEcon((prev) => {
        if (!prev) return prev;
        const suppliers = (prev.suppliers || []).map((s) => ({
          ...s,
          molecules: (s.molecules || []).map((m) => (
            m.gh === gh ? { ...m, keep_current: keep, best_alternative: keep ? null : m.best_alternative } : m
          )),
        }));
        return { ...prev, suppliers };
      });
      toast.success(keep ? "Molécula fijada a su proveedor actual" : "Vuelve a compararse con otros labs");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setKeepBusy(null);
    }
  };
  const [respectVolumes, setRespectVolumes] = useState(false);
  const [strategy, setStrategy] = useState("stock");
  const [savingStrategy, setSavingStrategy] = useState(false);
  const [openMolLab, setOpenMolLab] = useState(null); // lab con el listado de moléculas desplegado
  const [selectedFrom, setSelectedFrom] = useState([]); // labs origen para traslado masivo
  const [bulkOpen, setBulkOpen] = useState(false);

  const toggleFrom = (lab) => {
    setSelectedFrom((s) => s.includes(lab) ? s.filter((x) => x !== lab) : [...s, lab]);
  };

  // Cargar la estrategia configurada del usuario al montar
  useEffect(() => {
    api.get("/user/settings").then((r) => {
      setStrategy(r.data?.auto_pref_lab_strategy || "stock");
    }).catch(() => {});
  }, []);

  const saveStrategy = async (v) => {
    const prev = strategy;
    setStrategy(v);  // optimistic
    setSavingStrategy(true);
    try {
      await api.patch("/user/settings", { auto_pref_lab_strategy: v });
      toast.success("Estrategia guardada · se aplicará al re-evaluar moléculas");
    } catch {
      setStrategy(prev);  // rollback
      toast.error("No se pudo guardar");
    } finally {
      setSavingStrategy(false);
    }
  };

  // Flag "tengo condición con este laboratorio": se marca aquí (fuente única)
  const [togglingLab, setTogglingLab] = useState(null);
  const toggleAgreement = async (lab, current) => {
    setTogglingLab(lab);
    try {
      await api.post("/supplier-agreements", { laboratorio: lab, has_agreement: !current });
      setEcon((prev) =>
        prev
          ? {
              ...prev,
              suppliers: prev.suppliers.map((x) =>
                x.laboratorio === lab ? { ...x, has_agreement: !current } : x,
              ),
            }
          : prev,
      );
      toast.success(!current ? `${lab} marcado con condición` : `${lab} sin condición`);
    } catch (e) {
      toast.error("No se pudo actualizar la condición");
    } finally {
      setTogglingLab(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/dispensations/${dispId}/supplier-economics`);
      setEcon(r.data);
    } catch (e) {
      toast.error("No se pudo cargar el análisis económico");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (dispId) load(); }, [dispId, refreshKey]);

  const runOptimize = async () => {
    setOptimizing(true);
    try {
      const r = await api.get(`/dispensations/${dispId}/supplier-optimize`, {
        params: { respect_volumes: respectVolumes },
      });
      setOpt(r.data);
      const moves = r.data.moves_log?.length || 0;
      toast.success(
        `Asignación óptima · beneficio ${fmtEur(r.data.total_benefit_eur)}` +
        (moves > 0 ? ` (${moves} moléculas movidas para cubrir volúmenes)` : ""),
      );
    } catch {
      toast.error("No se pudo optimizar");
    } finally {
      setOptimizing(false);
    }
  };

  if (loading) {
    return <div className="bg-white border border-slate-200 rounded-lg p-6 grid place-items-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }
  if (!econ?.suppliers?.length) return null;

  // Filtrar para mostrar solo labs con condición o con potencial
  const withAgreement = econ.suppliers.filter((s) => s.has_agreement || s.discount_avg_pct > 0 || s.benefit_best_eur > 0);
  const sumBenefit = econ.suppliers.reduce((acc, s) => acc + (s.benefit_best_eur || 0), 0);
  const alerts = econ.suppliers.filter((s) => s.min_monthly_volume_eur > 0 && !s.meets_volume);

  return (
    <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-xl p-5 sm:p-6 mb-4" data-testid="supplier-economics-panel">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-sky-300">Análisis económico</div>
          <h3 className="text-xl font-bold mt-1">Beneficio con tus condiciones de compra</h3>
        </div>
        <button
          onClick={runOptimize}
          disabled={optimizing}
          className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="btn-optimize-suppliers"
        >
          {optimizing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Optimizar asignación
        </button>
      </div>

      <label className="inline-flex items-center gap-2 text-xs text-slate-200 mb-3 cursor-pointer" data-testid="toggle-respect-volumes">
        <input
          type="checkbox"
          checked={respectVolumes}
          onChange={(e) => setRespectVolumes(e.target.checked)}
          className="h-3.5 w-3.5"
        />
        Mover moléculas si hace falta para cumplir el <strong>volumen mensual mínimo</strong> de cada proveedor (greedy)
      </label>

      {/* Estrategia de auto-asignación del laboratorio sugerido por G.H. */}
      <div className="bg-slate-800/60 rounded-md p-3 mb-4" data-testid="strategy-selector">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2">
          Estrategia del laboratorio sugerido (AUTO) para moléculas solo-marca
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { v: "stock", label: "📦 Por stock", desc: "Lab con más unidades disponibles (default)" },
            { v: "benefit", label: "💰 Por beneficio €", desc: "Lab con mayor descuento aplicable" },
            { v: "balanced", label: "⚖️ Equilibrado", desc: "Stock × beneficio (recomendado si tienes vademecum cargado)" },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => saveStrategy(opt.v)}
              disabled={savingStrategy}
              title={opt.desc}
              data-testid={`strategy-${opt.v}`}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                strategy === opt.v
                  ? "bg-sky-500 border-sky-400 text-white"
                  : "bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="text-[11px] text-slate-400 mt-2">
          {strategy === "stock" && "Se usa el lab con más unidades en stock (criterio actual)."}
          {strategy === "benefit" && "Se prioriza el lab con mayor descuento (% medio + vademecum)."}
          {strategy === "balanced" && "Se combina ambos: lab con buen descuento Y stock disponible."}
          {" "}Aplica en el próximo upload de estudio/stock.
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-slate-800/60 rounded-md p-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Beneficio actual ({econ.months}m)</div>
          <div className="text-2xl font-bold mt-1 font-mono-num">{fmtEur(sumBenefit)}</div>
          <div className="text-xs text-slate-400 mt-1">{withAgreement.length} proveedor(es) con condición</div>
        </div>
        {opt && (
          <div className="bg-emerald-500/15 border border-emerald-400/40 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-300">Beneficio óptimo simulado</div>
            <div className="text-2xl font-bold mt-1 font-mono-num text-emerald-200">{fmtEur(opt.total_benefit_eur)}</div>
            <div className="text-xs text-emerald-200 mt-1">{opt.assignments.length} moléculas reasignadas</div>
          </div>
        )}
        {alerts.length > 0 && (
          <div className="bg-amber-500/15 border border-amber-400/40 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-300 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Volumen no cumplido</div>
            <div className="text-2xl font-bold mt-1 font-mono-num text-amber-200">{alerts.length}</div>
            <div className="text-xs text-amber-200 mt-1">proveedor(es) por debajo del mínimo</div>
          </div>
        )}
      </div>

      {/* Banner traslado masivo */}
      {selectedFrom.length > 0 && (
        <div className="mb-3 px-4 py-2.5 bg-purple-600 text-white rounded-lg shadow flex flex-wrap items-center gap-3" data-testid="bulk-transfer-cta">
          <span className="text-sm font-semibold flex-1 min-w-0">
            <strong className="font-mono-num">{selectedFrom.length}</strong> laboratorio{selectedFrom.length > 1 ? "s" : ""} origen · <span className="opacity-90 text-xs">{selectedFrom.join(" · ")}</span>
          </span>
          <button
            onClick={() => setBulkOpen(true)}
            className="inline-flex items-center gap-1.5 bg-white text-purple-700 hover:bg-purple-50 font-bold px-3.5 py-1.5 rounded-full text-xs whitespace-nowrap"
            data-testid="btn-open-bulk-transfer"
          >
            <Send className="h-3.5 w-3.5" /> Traslado masivo
          </button>
          <button
            onClick={() => setSelectedFrom([])}
            className="text-white/70 hover:text-white text-xs"
            title="Deseleccionar todo"
            data-testid="btn-clear-selection"
          >
            ✕
          </button>
        </div>
      )}

      {/* Listado de proveedores con condición */}
      <div className="space-y-2">
        {econ.suppliers.map((s) => {
          const cond = s.has_agreement;
          const molsOpen = openMolLab === s.laboratorio;
          const mols = s.molecules || [];
          const nMonths = s.months || econ.months || 1;
          return (
            <div
              key={s.laboratorio}
              className={`rounded-md p-3 ${cond ? "bg-white/10" : "bg-slate-800/40"} ${selectedFrom.includes(s.laboratorio) ? "ring-2 ring-purple-400/70" : ""}`}
              data-testid={`econ-row-${s.laboratorio}`}
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="min-w-0 flex-1 flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedFrom.includes(s.laboratorio)}
                    onChange={() => toggleFrom(s.laboratorio)}
                    className="w-4 h-4 accent-purple-500"
                    title="Seleccionar para traslado masivo"
                    data-testid={`from-check-${s.laboratorio}`}
                  />
                  <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate flex items-center gap-2 flex-wrap">
                    {s.laboratorio}
                    <button
                      onClick={() => toggleAgreement(s.laboratorio, cond)}
                      disabled={togglingLab === s.laboratorio}
                      title={cond ? "Tienes condición con este laboratorio · pulsa para quitarla" : "Sin condición · pulsa para marcar que le compras con condición"}
                      className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border transition-colors disabled:opacity-50 ${
                        cond
                          ? "bg-emerald-500/20 text-emerald-100 border-emerald-400/40 hover:bg-emerald-500/30"
                          : "bg-slate-700/60 text-slate-300 border-slate-500/40 hover:bg-slate-600/60"
                      }`}
                      data-testid={`agreement-flag-${s.laboratorio}`}
                    >
                      {cond ? "✓ Con condición" : "Sin condición"}
                    </button>
                    {cond && (s.effective_pct > 0 || s.total_compra_pvl_eur > 0) && (
                      <span
                        className="text-[10px] uppercase font-bold bg-emerald-400/20 text-emerald-200 px-2 py-0.5 rounded-full"
                        title="Descuento efectivo aplicado sobre lo que has comprado a este laboratorio (beneficio real / € PVL comprados)"
                      >
                        {(s.effective_pct ?? 0).toFixed(2).replace(".", ",")}% efectivo
                      </span>
                    )}
                    {cond && s.total_compra_pvl_eur === 0 && s.discount_avg_pct > 0 && (
                      <span
                        className="text-[10px] uppercase font-bold bg-emerald-400/20 text-emerald-200 px-2 py-0.5 rounded-full"
                        title="Media aritmética de los descuentos del vademecum (sin compras aún para calcular el % efectivo)"
                      >
                        {s.discount_avg_pct}% medio
                      </span>
                    )}
                    {s.linear_discount_pct > 0 && (
                      <span
                        className="text-[10px] uppercase font-bold bg-violet-400/20 text-violet-200 px-2 py-0.5 rounded-full"
                        title="Descuento lineal aplicado a moléculas sin % específico en el vademecum"
                      >
                        {s.linear_discount_pct}% lineal
                      </span>
                    )}
                    {s.min_monthly_volume_eur > 0 && (
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
                        s.meets_volume
                          ? "bg-sky-400/20 text-sky-200"
                          : "bg-amber-400/30 text-amber-100"
                      }`}>
                        Min {fmtEur(s.min_monthly_volume_eur)}/mes
                        {!s.meets_volume && ` · falta ${fmtEur(s.volume_gap_eur)}`}
                      </span>
                    )}
                    {s.no_purchases_in_study && (
                      <span className="text-[10px] uppercase font-bold bg-slate-500/30 text-slate-100 px-2 py-0.5 rounded-full"
                        title="Este laboratorio tiene condición cargada pero aún no le has comprado nada en el estudio actual"
                      >
                        Sin compras aún
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-300 mt-0.5 font-mono-num">
                    {fmtEur(s.total_compra_pvl_eur)} PVL · {s.per_month_pvl_eur.toLocaleString("es-ES")}€/mes · {mols.length} G.H.
                  </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-[10px] uppercase font-bold text-slate-400">Beneficio</div>
                    <div className="font-mono-num font-bold text-emerald-300">{fmtEur(s.benefit_best_eur)}</div>
                  </div>
                  <button
                    onClick={() => setOpenMolLab(molsOpen ? null : s.laboratorio)}
                    className="px-3 py-1.5 bg-slate-700/60 hover:bg-slate-600 text-white rounded-md text-xs font-semibold inline-flex items-center gap-1"
                    data-testid={`btn-molecules-${s.laboratorio}`}
                  >
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${molsOpen ? "rotate-180" : ""}`} />
                    Moléculas
                  </button>
                  <button
                    onClick={() => onConfigure?.(s.laboratorio)}
                    className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-md text-xs font-semibold inline-flex items-center gap-1"
                    data-testid={`btn-configure-${s.laboratorio}`}
                  >
                    <Settings2 className="h-3.5 w-3.5" /> Condiciones
                  </button>
                </div>
              </div>
              {molsOpen && (
                <div className="mt-3 bg-slate-950/40 rounded-md p-3 overflow-x-auto" data-testid={`molecules-list-${s.laboratorio}`}>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-sky-300 mb-2">
                    Volumen dispensado por molécula · para negociar con otro laboratorio
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-slate-400 uppercase text-[9px] tracking-wider">
                        <th className="text-left py-1 pr-2 font-semibold">Grupo Homogéneo</th>
                        <th className="text-right py-1 px-2 font-semibold">Uds ({nMonths}m)</th>
                        <th className="text-right py-1 px-2 font-semibold">Uds/mes</th>
                        <th className="text-right py-1 px-2 font-semibold">€ PVL</th>
                        <th className="text-right py-1 px-2 font-semibold">€ PVL/mes</th>
                        <th className="text-right py-1 px-2 font-semibold">Dto. actual</th>
                        <th className="text-left py-1 pl-3 font-semibold">Mejor alternativa <span className="normal-case font-normal text-slate-500">· fijar proveedor</span></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {[...mols].sort((a, b) => (b.eur || 0) - (a.eur || 0)).map((m) => (
                        <tr key={m.gh}>
                          <td className="py-1.5 pr-2 text-slate-200 max-w-[380px] truncate" title={m.gh}>
                            {m.gh || "—"}
                            {m.is_excluded && (
                              <span className="ml-1.5 text-[9px] uppercase font-bold bg-rose-500/20 text-rose-300 border border-rose-400/30 rounded px-1 py-0.5" title="El laboratorio tiene esta molécula excluida de su tarifa (0%)" data-testid={`excluded-badge-${m.gh}`}>
                                sin dto
                              </span>
                            )}
                          </td>
                          <td className="py-1.5 px-2 text-right font-mono-num text-slate-100">{Number(m.units || 0).toLocaleString("es-ES")}</td>
                          <td className="py-1.5 px-2 text-right font-mono-num text-slate-300">{(Number(m.units || 0) / nMonths).toLocaleString("es-ES", { maximumFractionDigits: 1 })}</td>
                          <td className="py-1.5 px-2 text-right font-mono-num text-slate-100">{fmtEur(m.eur)}</td>
                          <td className="py-1.5 px-2 text-right font-mono-num font-bold text-sky-200">{fmtEur((m.eur || 0) / nMonths)}</td>
                          <td className="py-1.5 px-2 text-right" data-testid={`current-discount-${m.gh}`}>
                            <div className="font-mono-num font-bold text-amber-200">
                              {Number(m.best_pct || 0).toLocaleString("es-ES", { maximumFractionDigits: 2 })}%
                            </div>
                            <div className="font-mono-num text-[10px] text-slate-300">{fmtEur(m.benefit_best_eur)}</div>
                            <div className="text-[9px] uppercase tracking-wider text-slate-500">
                              {Number(m.best_pct || 0) > 0 ? (SOURCE_LABEL[m.best_source] || "—") : (m.is_excluded ? "excluida" : "sin dto")}
                            </div>
                          </td>
                          <td className="py-1.5 pl-3" data-testid={`best-alt-${m.gh}`}>
                            <div className="flex items-start gap-2">
                              <div className="min-w-0 flex-1">
                                {m.keep_current ? (
                                  <span className="text-[10px] uppercase font-bold bg-amber-500/15 text-amber-300 border border-amber-400/30 rounded px-1.5 py-0.5" data-testid={`keep-badge-${m.gh}`}>
                                    Mantener actual
                                  </span>
                                ) : m.best_alternative ? (
                                  <>
                                    <div className="text-emerald-300 font-semibold truncate max-w-[200px]" title={m.best_alternative.lab}>
                                      {m.best_alternative.lab}
                                    </div>
                                    <div className="font-mono-num text-[11px] text-emerald-200">
                                      {Number(m.best_pct || 0).toLocaleString("es-ES", { maximumFractionDigits: 2 })}%
                                      <span className="text-slate-400 mx-1">→</span>
                                      <span className="font-bold">{Number(m.best_alternative.pct || 0).toLocaleString("es-ES", { maximumFractionDigits: 2 })}%</span>
                                    </div>
                                    <div className="text-[10px] text-emerald-400/80 font-mono-num">
                                      +{fmtEur(m.best_alternative.extra_vs_current_eur)} vs actual
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-slate-500">—</span>
                                )}
                              </div>
                              <button
                                onClick={() => toggleKeepCurrent(m.gh, !m.keep_current)}
                                disabled={keepBusy === m.gh}
                                className={`shrink-0 p-1 rounded transition-colors ${
                                  m.keep_current
                                    ? "text-amber-300 hover:text-amber-200 bg-amber-500/10"
                                    : "text-slate-500 hover:text-sky-300"
                                }`}
                                title={m.keep_current
                                  ? "Volver a comparar con otros laboratorios"
                                  : "Mantener el proveedor actual para esta molécula (no sugerir cambio)"}
                                data-testid={`keep-toggle-${m.gh}`}
                              >
                                {keepBusy === m.gh
                                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  : m.keep_current ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t border-white/20">
                        <td className="py-1.5 pr-2 font-bold text-white">TOTAL</td>
                        <td className="py-1.5 px-2 text-right font-mono-num font-bold text-white">{Number(s.total_units || 0).toLocaleString("es-ES")}</td>
                        <td className="py-1.5 px-2 text-right font-mono-num font-bold text-slate-200">{(Number(s.total_units || 0) / nMonths).toLocaleString("es-ES", { maximumFractionDigits: 1 })}</td>
                        <td className="py-1.5 px-2 text-right font-mono-num font-bold text-white">{fmtEur(s.total_compra_pvl_eur)}</td>
                        <td className="py-1.5 px-2 text-right font-mono-num font-bold text-sky-200">{fmtEur(s.per_month_pvl_eur)}</td>
                        <td className="py-1.5 px-2 text-right font-mono-num font-bold text-amber-200" data-testid={`total-current-discount-${s.laboratorio}`}>{fmtEur(s.benefit_best_eur)}</td>
                        <td className="py-1.5 pl-3"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Resultado del optimizador (top 10) */}
      {opt && opt.assignments?.length > 0 && (
        <div className="mt-5 bg-slate-800/60 rounded-md p-4">
          <div className="text-xs uppercase tracking-widest font-bold text-emerald-300 mb-3 flex items-center gap-1">
            <TrendingUp className="h-4 w-4" /> Top 10 asignaciones óptimas
          </div>
          <div className="space-y-1 text-sm">
            {opt.assignments.slice(0, 10).map((a) => (
              <div key={a.gh} className="flex items-center justify-between gap-2 py-1 border-b border-white/5 last:border-b-0" data-testid={`opt-assignment-${a.gh}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{a.gh}</div>
                  <div className="text-[11px] text-slate-400">
                    {a.recommended_lab} · {a.discount_pct}% ({a.source})
                  </div>
                </div>
                <div className="font-mono-num font-bold text-emerald-300">+{fmtEur(a.benefit_eur)}</div>
              </div>
            ))}
          </div>
          {opt.volume_alerts?.length > 0 && (
            <div className="mt-3 bg-amber-500/15 border border-amber-400/40 rounded-md p-3">
              <div className="text-xs font-bold text-amber-200 inline-flex items-center gap-1 mb-1">
                <AlertTriangle className="h-3 w-3" /> Avisos de volumen tras reasignar
              </div>
              <ul className="text-xs text-amber-100 space-y-0.5">
                {opt.volume_alerts.map((a) => (
                  <li key={a.laboratorio} data-testid={`volume-alert-${a.laboratorio}`}>
                    {a.laboratorio}: {fmtEur(a.expected_monthly_eur)}/mes ofrecido vs {fmtEur(a.required_monthly_eur)}/mes requerido (falta {fmtEur(a.gap_eur)})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {bulkOpen && (
        <BulkTransferCascadeModal
          fromLabs={selectedFrom}
          allLabs={(econ?.suppliers || []).map((s) => s.laboratorio)}
          onClose={() => setBulkOpen(false)}
          onApplied={() => { setBulkOpen(false); setSelectedFrom([]); load(); }}
        />
      )}
    </div>
  );
}
